import React, {useState} from 'react';
import {Card, CardContent, CardHead} from "../atoms/Card";
import {CheckIcon, ExclamationIcon, RefreshIcon} from "@heroicons/react/outline";
import {Button, Text, Input, Flex, Box, VStack, HStack, Stack, Tooltip} from "@chakra-ui/react";
import {VerifiedDomainEntity} from '@app/core-app/app/data/verified-email-entity';
import {DomainVerification} from "@app/core-app/app/data/profile";
import {CopyIcon} from "@chakra-ui/icons";
import {useStatusToast} from "../../../hooks/useStatusToast";
import {DkimGenerator} from "@app/core-app/app/service/dkim-generator";

export type VerifiedDomainCardProps = {
    domainEntity: VerifiedDomainEntity | null;
    domainVerification: DomainVerification | null;
    formActive: boolean,
    onConnectDomain: (domain: string) => Promise<void>;
    onDisconnectDomain: () => Promise<void>;
    onRefreshStatus: () => Promise<void>;
};

export const VerifiedDomainCard = ({
  domainEntity,
  domainVerification,
  formActive,
  onConnectDomain,
  onDisconnectDomain,
  onRefreshStatus
}: VerifiedDomainCardProps) => {
  const [domainInput, setDomainInput] = useState('');

  const [progressing, setProgressing] = useState(false)
  const {addToast, updateToastResult, toastCatch} = useStatusToast()

  const handleConnect = () => {
    if(domainInput) {
      setProgressing(true)
      onConnectDomain(domainInput.toLowerCase())
        .then(() => {
          setProgressing(false)
          setDomainInput('')
        })
    }
  };

  const domainRecord = DkimGenerator.formatTXTRecord(domainVerification?.pubKey!)

  const DomainRecord = ({token, domainRecord}: { token: string, domainRecord: string }) => {

    const host = `${token}._domainkey.${domainEntity?.domain}`
    return <Stack direction={"column"} alignItems={"start"}>

      <Text>TXT Record Name:</Text>
      <Flex alignItems={"center"}>
        <Text fontSize={"sm"} rounded={"md"} p={1} bgColor={"gray.100"} maxW={"300px"} noOfLines={1}>{host}</Text>
        <Button colorScheme="gray" size="sm" variant={"text"} onClick={() => {
          addToast({title: "Copied to clipboard", status: "success"})
          navigator.clipboard.writeText(host)
        }}><CopyIcon/></Button>
      </Flex>
      <Text>TXT Record Value: </Text>
      <Flex alignItems={"center"}>
        <Text fontSize={"sm"} rounded={"md"} p={1} bgColor={"gray.100"} maxW={"300px"} noOfLines={1}>{domainRecord}</Text>
        <Button colorScheme="gray" size="sm" variant={"text"} onClick={() => {
          addToast({title: "Copied to clipboard", status: "success"})
          navigator.clipboard.writeText(domainRecord)
        }}><CopyIcon/></Button>
      </Flex>
    </Stack>
  }

  const StatusLabel = ({type}: {type: "Pending" | "Success" | "Failed"}) => {

    if(type == "Success"){
      return <Text bgColor={"green.200"} px={1} fontSize={"sm"} rounded={"md"}>{type}</Text>
    }
    if(type === "Failed"){
      return <Text bgColor={"red.200"} px={1} fontSize={"sm"} rounded={"md"}>{type}</Text>
    }  
    
    return <Text bgColor={"orange.200"} px={1} fontSize={"sm"} rounded={"md"}>{type}</Text>
  }

  return (
    <Card>
      <CardHead justify>

        {domainEntity ? (
          <HStack spacing={1} alignItems="center">
            <CheckIcon className="h-6 w-6 text-green-700"/>
            <Text>Connected to {domainEntity.domain} </Text>
          </HStack>
        ) : (
          <Flex alignItems="center">
            <ExclamationIcon className="h-6 w-6 text-orange-500 mr-1"/>
            <Text>No Domain Connected</Text>
          </Flex>
        )}

        <Stack direction={"row"}>
          {!!domainEntity && domainEntity.verificationStatus !== 'Success' && (
            <Button colorScheme="gray" variant={"outline"} size="sm" onClick={onRefreshStatus}>
              <RefreshIcon className="h-4 w-4 mr-2"/>  <span>Refresh </span>
            </Button>
          )}
          {!!domainEntity &&
                      <Button colorScheme="gray" variant={"outline"} size="sm" onClick={onDisconnectDomain}>
                        <span>Disconnect </span>
                      </Button>
          }
        </Stack>


      </CardHead>
      <CardContent>
        <Text textColor={"pink.500"} fontSize={"sm"}>Premium feature</Text>

        {domainEntity ? (
          <VStack align="start" spacing={2}>
            <Stack spacing={3} my={2}>
              <Stack direction={"row"} alignItems={"center"}>
                <Text>Domain verification: </Text>
                {domainEntity.verificationStatus == "Success" && <StatusLabel type={"Success"}/>}
                {domainEntity.verificationStatus == "Pending" && <StatusLabel type={"Pending"}/>}
                {domainEntity.verificationStatus == "Failed" && <StatusLabel type={"Failed"}/>}

              </Stack>
              <Stack direction={"row"} alignItems={"center"}>
                <Text>DKIM verification: </Text>
                {domainEntity.dkimVerificationStatus == "Success" && <StatusLabel type={"Success"}/>}
                {domainEntity.dkimVerificationStatus == "Pending" && <StatusLabel type={"Pending"}/>}
                {domainEntity.verificationStatus == "Failed" && <StatusLabel type={"Failed"}/>}
              </Stack>
            </Stack>

            {domainEntity.verificationStatus == "Failed" && <Text color={"orange.500"}>Please disconnect and connect your domain again.</Text>}


            <Text maxW={"700px"}>If you manage {domainEntity?.domain ?? "domain"} and you have access to your
              DNS records, you can create a new TXT record with the following values:</Text>

            <Box my={2} borderLeft={"2px solid"} borderColor={"blue.500"} py={2} px={3}>
              {domainEntity.dkimTokens.map(token => (
                <DomainRecord key={token} token={token} domainRecord={domainRecord}/>
              ))}
            </Box>

            <Text>
              It may take up to 72h for domain to be verified, but typically takes about 15 minutes.
            </Text>
          </VStack>

        ) : (

          <VStack mt={1} align="start" maxW={"md"}>
            <Text> Enter your domain below to link it with NotionSender.
                            You will be able to manage your email sending address and enable DKIM signatures.
            </Text>
            <Input
              placeholder="Enter your domain"
              size={"sm"}
              rounded={"md"}
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
            />
            <Tooltip isDisabled={formActive} hasArrow bg={"pink.500"} label={"This is premium feature. Upgrade to enable."}>
              <Button colorScheme="blue" mt={2} size={"sm"}
                isDisabled={!formActive}
                isLoading={progressing}
                onClick={handleConnect}
              >
                Connect Domain
              </Button>
            </Tooltip>
          </VStack>
        )}
      </CardContent>
    </Card>
  );
};
